// Inpatient ADT and the bed board (docs/plans/phase2-inpatient-adt.md §3.3).
import { queryOptions } from '@tanstack/react-query';
import {
  admissionSchema,
  adminWardSchema,
  bedBoardSchema,
  pageSchema,
  serviceItemRowSchema,
  type AdmitBody,
  type createBedBodySchema,
  type createRoomBodySchema,
  type createWardBodySchema,
  type DischargeBody,
  type moveBodySchema,
  type setBedStatusBodySchema,
  type updateBedBodySchema,
  type updateWardBodySchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

/** The board refreshes every 30 seconds, like the queue board. */
export const bedBoardQuery = queryOptions({
  queryKey: ['inpatient', 'bed-board'],
  queryFn: () => api('GET', '/inpatient/bed-board', { schema: bedBoardSchema }),
  refetchInterval: 30_000,
});

export const admissionQuery = (admissionId: string) =>
  queryOptions({
    queryKey: ['inpatient', 'admission', admissionId],
    queryFn: () => api('GET', `/admissions/${admissionId}`, { schema: admissionSchema }),
  });

export const admitPatient = (visitId: string, body: AdmitBody) =>
  api('POST', `/visits/${visitId}/admissions`, { body, schema: admissionSchema });

export const movePatient = (admissionId: string, body: In<typeof moveBodySchema>) =>
  api('POST', `/admissions/${admissionId}/moves`, { body, schema: admissionSchema });

export const dischargePatient = (admissionId: string, body: DischargeBody) =>
  api('POST', `/admissions/${admissionId}/discharge`, { body, schema: admissionSchema });

export const cancelAdmission = (admissionId: string, reason: string) =>
  api('POST', `/admissions/${admissionId}/cancel`, {
    body: { reason },
    schema: admissionSchema,
  });

export const setBedStatus = (bedId: string, body: In<typeof setBedStatusBodySchema>) =>
  api('POST', `/inpatient/beds/${bedId}/status`, {
    body,
    schema: z.object({ id: z.uuid(), status: z.string() }),
  });

// --- Settings > Wards and beds ----------------------------------------------------------------

export const adminWardsQuery = queryOptions({
  queryKey: ['admin', 'wards'],
  queryFn: () => api('GET', '/admin/wards', { schema: z.array(adminWardSchema) }),
});

/** Active bed-day services, the charge a ward can use. */
export const bedDayItemsQuery = queryOptions({
  queryKey: ['catalogue', 'bed-day-items'],
  queryFn: () =>
    api('GET', '/catalogue/items?category=bed_day&status=active&limit=100', {
      schema: pageSchema(serviceItemRowSchema),
    }),
});

export const createWard = (body: In<typeof createWardBodySchema>) =>
  api('POST', '/admin/wards', { body, schema: adminWardSchema });

export const updateWard = (wardId: string, body: In<typeof updateWardBodySchema>) =>
  api('PATCH', `/admin/wards/${wardId}`, { body, schema: adminWardSchema });

export const createRoom = (wardId: string, body: In<typeof createRoomBodySchema>) =>
  api('POST', `/admin/wards/${wardId}/rooms`, { body, schema: adminWardSchema });

export const createBed = (wardId: string, body: In<typeof createBedBodySchema>) =>
  api('POST', `/admin/wards/${wardId}/beds`, { body, schema: adminWardSchema });

export const updateBed = (bedId: string, body: In<typeof updateBedBodySchema>) =>
  api('PATCH', `/admin/beds/${bedId}`, { body, schema: adminWardSchema });
